import { useState } from 'react';
import {
  Play,
  ShoppingCart,
  Headphones,
  BookOpen,
  Package,
  ArrowRight,
  Check,
  Lightbulb,
  CalendarClock,
} from 'lucide-react';
import { Button, FeatureIcon, Badge } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { SectionDivider } from '@/components/decorative';
import { TelegramIcon } from '@/components/icons';

const demos = [
  {
    title: 'Support Assistant',
    subtitle: 'Finance',
    description:
      'Your go-to assistant for everything about DialogMe — pricing, features, getting started, integrations, security, and troubleshooting. Powered by a knowledge base of 20 support articles.',
    icon: Headphones,
    features: [
      'Answer questions about DialogMe platform and features',
      'Explain pricing tiers, integrations, and service types',
      'Guide you through getting started and setup',
      'Help with troubleshooting and account issues',
    ],
    examples: [
      'What is DialogMe and how does it work?',
      'What pricing plans are available?',
      'How do I set up a Telegram bot?',
      'What is MCP and why does it matter?',
    ],
    link: 'https://t.me/dialogme_support_demo_bot',
    gradient: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-400',
    accentColor: 'border-l-blue-500/50',
  },
  {
    title: 'Shop Assistant',
    subtitle: 'E-Commerce',
    description:
      'An electronics store assistant with 100 products across Audio, Smart Home, Wearables, and Accessories. Browse, compare, and get recommendations — all through conversation.',
    icon: ShoppingCart,
    features: [
      'Browse products by category, price, or feature',
      'Compare products and get recommendations',
      'Answer questions about product specs and pricing',
      'Help find the right product for your needs',
    ],
    examples: [
      'Show me wireless headphones under $100',
      'What smart home devices do you have?',
      'Compare FitBand Ultra and PulseWatch GT',
      'I need a gift under $50',
    ],
    link: 'https://t.me/dialogme_ecommerce_demo_bot',
    gradient: 'from-emerald-500/20 to-teal-500/20',
    iconColor: 'text-emerald-400',
    accentColor: 'border-l-emerald-500/50',
  },
  {
    title: 'Blog Assistant',
    subtitle: 'Blog',
    description:
      'A tech blog assistant with 10 articles on conversational AI, LLMs, RAG, tool calling, and the future of customer interaction. Discover, summarize, and discuss content naturally.',
    icon: BookOpen,
    features: [
      'Browse and search articles by topic',
      'Summarize blog posts on demand',
      'Explain technical concepts from articles',
      'Suggest related reading based on your interests',
    ],
    examples: [
      'What articles do you have?',
      'Explain how RAG works for AI assistants',
      'What did you write about Telegram?',
      'Summarize the post about tool calling',
    ],
    link: 'https://t.me/dialogme_blog_demo_bot',
    gradient: 'from-purple-500/20 to-pink-500/20',
    iconColor: 'text-purple-400',
    accentColor: 'border-l-purple-500/50',
  },
  {
    title: 'Offline Business Assistant',
    subtitle: 'Offline Business',
    description:
      'A conversational front desk for offline businesses — travel agencies, clinics, salons, studios, and workshops. Browse services, book appointments and consultations, get reminders, and raise requests, replacing the phone calls and walk-ins with a single chat.',
    icon: CalendarClock,
    features: [
      'Browse services with prices, duration, and availability',
      'Book, confirm, and reschedule appointments in chat',
      'Get reminders and answers about hours and policies',
      'Raise tickets for special requests and human handoff',
    ],
    examples: [
      'What services do you offer?',
      'Book a consultation for me',
      'What are your opening hours?',
      'How do I pay and where are you located?',
    ],
    link: 'https://t.me/dialogme_offline_b_demo_bot',
    gradient: 'from-amber-500/20 to-orange-500/20',
    iconColor: 'text-amber-400',
    accentColor: 'border-l-amber-500/50',
  },
];


const filters = ['All', ...demos.map((d) => d.subtitle)];

export function DemoPage() {
  const [activeFilter, setActiveFilter] = useState('All');

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-10 md:py-20">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 rounded-full mb-6 border border-indigo-500/20">
            <Play className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Live Demos</span>
          </div>
          <h1 className="text-hero-mobile md:text-hero-desktop font-extrabold tracking-heading mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
            Don&apos;t Take Our Word for It
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Try a live bot. Ask anything. See what conversation can do.
          </p>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 md:mb-12">
          {filters.map((filter) => {
            const isActive = activeFilter === filter;
            return (
              <button
                key={filter}
                type="button"
                onClick={() => setActiveFilter(filter)}
                className={`text-[13px] font-medium rounded-full px-3.5 py-1.5 border transition-all duration-200 ${
                  isActive
                    ? 'bg-indigo-500/20 border-indigo-400/40 text-white'
                    : 'bg-slate-800/40 border-slate-700/50 text-slate-400 hover:text-slate-200 hover:border-slate-600'
                }`}
              >
                {filter}
              </button>
            );
          })}
        </div>

        {/* Demo Cards */}
        <div className="mb-12 md:mb-16">
          {demos.map((demo, index) => {
            const Icon = demo.icon;
            const isVisible = activeFilter === 'All' || activeFilter === demo.subtitle;
            return (
              <div
                key={index}
                className={`grid transition-all duration-500 ease-out ${
                  isVisible
                    ? 'grid-rows-[1fr] opacity-100 mb-6 sm:mb-8'
                    : 'grid-rows-[0fr] opacity-0 mb-0 pointer-events-none'
                }`}
              >
                <div className="overflow-hidden min-h-0">
                  <Card variant="elevated" hoverable className={`group p-5 sm:p-6 md:p-8 border-l-[3px] ${demo.accentColor}`}>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    <FeatureIcon gradient={demo.gradient} size="lg">
                      <Icon className={`w-8 h-8 ${demo.iconColor}`} />
                    </FeatureIcon>
                  </div>

                  <div className="flex-1">
                    <div className="mb-4">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-100 tracking-tight group-hover:text-white transition-colors">
                          {demo.title}
                        </h2>
                        <Badge variant="premium">{demo.subtitle}</Badge>
                      </div>
                    </div>

                    <p className="text-sm sm:text-base text-slate-400 mb-5 sm:mb-6 leading-relaxed group-hover:text-slate-300 transition-colors">{demo.description}</p>

                    <div className="mb-5 sm:mb-6">
                      <h3 className="text-sm font-semibold text-slate-300 mb-3">What you can do:</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {demo.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Not Sure Where to Start? */}
                    <div className="mb-5 sm:mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb className="w-4 h-4 text-amber-400" />
                        <h3 className="text-sm font-semibold text-slate-300">Not sure where to start? Try these:</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {demo.examples.map((example, idx) => (
                          <span
                            key={idx}
                            className="inline-block text-[13px] text-slate-400 bg-slate-700/40 border border-slate-600/30 rounded-full px-3.5 py-1.5 cursor-default transition-all duration-200 hover:bg-indigo-500/20 hover:border-indigo-400/40 hover:text-white"
                          >
                            &ldquo;{example}&rdquo;
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                      <a href={demo.link} target="_blank" rel="noopener noreferrer">
                        <Button
                          variant="primary"
                          size="md"
                          className="w-full sm:w-auto"
                          leftIcon={<TelegramIcon className="w-4 h-4" />}
                          rightIcon={<ArrowRight className="w-4 h-4" />}
                        >
                          Try Demo on Telegram
                        </Button>
                      </a>
                      <span className="inline-flex items-center justify-center text-xs text-slate-500 px-3 py-1.5 rounded-full border border-slate-700/50 bg-slate-700/20">
                        More channels coming soon
                      </span>
                    </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            );
          })}
        </div>

        <SectionDivider variant="gradient" />

        {/* Bottom CTA */}
        <Card variant="cta" className="text-center p-6 sm:p-8 md:p-10">
          <FeatureIcon
            gradient="from-indigo-500/30 to-purple-500/30"
            size="lg"
            className="mx-auto mb-6"
          >
            <Package className="w-8 h-8 text-indigo-400" />
          </FeatureIcon>
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-slate-100 mb-3">Questions? We&apos;ve Got Answers.</h2>
          <p className="text-sm sm:text-base text-slate-300 mb-6 max-w-xl mx-auto leading-relaxed">
            Drop us a line. We respond fast.
          </p>
          <a href="mailto:sales@dialogme.ai">
            <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-4 h-4" />}>
              Ask Us
            </Button>
          </a>
        </Card>
      </div>
    </main>
  );
}
